import React from 'react';
import { Icon } from './Icon';
import type { IconName } from './Icon';
import UploadIndicator, { uploadCaption } from './UploadIndicator';
import { getUploadLabels } from '../lib/fileValidation';
import type { UploadProgress } from '../lib/upload/retry';

const TYPE_ICON: Record<string, IconName> = {
    image: 'image',
    pdf: 'description',
    stl: 'deployed_code',
    other: 'draft',
};

export interface FileAttachmentRowProps {
    name: string;
    /** File type — drives the leading icon. Falls back to ``other`` if unknown. */
    type?: 'image' | 'pdf' | 'stl' | 'other' | string;
    /** Direct URL — backs the download <a>. */
    url?: string;
    /** Alternative download handler — used when the file requires an
     *  authenticated request (Bearer / signed fetch on click). Mutually
     *  exclusive with ``url`` (``url`` wins if both are passed). */
    onDownload?: () => void;
    /** When set, renders a 3D viewer button BEFORE the download button.
     *  The caller decides what "preview" means (opens a modal, navigates,
     *  etc.). Only shown when ``name`` matches ``*.stl`` / ``*.3mf``. */
    onPreview3D?: () => void;
    /** Visual state. ``pending`` = uploaded but not yet saved (green tint).
     *  ``deleted`` = marked for removal but still shown for undo (red tint). */
    state?: 'pending' | 'deleted';
    onDelete?: () => void;
    onUndoDelete?: () => void;
    preview3DAriaLabel?: string;
    downloadAriaLabel?: string;
    deleteAriaLabel?: string;
    undoAriaLabel?: string;
    /** Extra controls rendered at the END of the action group, after delete.
     *  Optional and purely additive so existing consumers are untouched. Use it
     *  for a row-level `OverflowMenu`; keep any icons at `!text-[16px]` so the
     *  row stays compact. */
    actions?: React.ReactNode;
    /** The file is still uploading: a progress ring at the right, the bytes
     *  under the name, and none of the other actions until it lands. A failed
     *  one shows «Reintentar» (`onRetryUpload`). */
    upload?: UploadProgress;
    onRetryUpload?: () => void;
    /** Cancels the upload in progress (or drops a failed one). */
    onCancelUpload?: () => void;
    className?: string;
}

/**
 * Standardised row for a single attached file. Used inside expense
 * modals, order item cards, design files sections, catalog — everywhere
 * a file needs to be listed with actions.
 *
 * Visual metrics are fixed so designs / orders / expenses look identical.
 * Actions are opt-in:
 *  - ``onPreview3D``: 3D viewer button (only renders when ``name`` is
 *    .stl / .3mf — STL design files in the catalog).
 *  - ``url`` or ``onDownload``: explicit download button (anchor or
 *    callback). The row body is NOT a click target — only the buttons
 *    are, to keep the click behaviour identical to the design list.
 *  - ``onDelete`` / ``onUndoDelete``: delete/undo.
 */
const FileAttachmentRow: React.FC<FileAttachmentRowProps> = ({
    name,
    type,
    url,
    onDownload,
    onPreview3D,
    state,
    onDelete,
    onUndoDelete,
    preview3DAriaLabel,
    downloadAriaLabel,
    deleteAriaLabel,
    undoAriaLabel,
    actions,
    upload,
    onRetryUpload,
    onCancelUpload,
    className = '',
}) => {
    const icon = TYPE_ICON[type ?? 'other'] ?? TYPE_ICON.other;
    const isPrintable3D = /\.(stl|3mf)$/i.test(name);

    const stateCls =
        state === 'pending'
            ? 'border-emerald-500/40 dark:border-emerald-500/50 bg-emerald-500/10 dark:bg-emerald-500/15'
            : state === 'deleted'
                ? 'border-red-500/40 dark:border-red-500/50 bg-red-500/10 dark:bg-red-500/15 opacity-80'
                : 'border-border-subtle dark:border-border-subtle-dark bg-white dark:bg-surface-dark';

    if (upload) {
        const labels = getUploadLabels();
        const failed = upload.kind === 'failed';
        // A refusal: same file, same answer — its reason, whole, and no retry.
        const refused = failed && upload.retryable === false;
        const captionCls = failed
            ? 'text-red-600 dark:text-red-400'
            : upload.kind === 'retrying'
                ? 'text-amber-600 dark:text-amber-400'
                : 'text-text-sub dark:text-slate-400';
        return (
            <div className={`flex items-center gap-2 rounded border p-1.5 text-xs border-border-subtle dark:border-border-subtle-dark bg-white dark:bg-surface-dark ${className}`}>
                <Icon name={icon} className="text-slate-400 shrink-0 !text-[16px]" />
                <div className="min-w-0 flex-1">
                    <div className="truncate text-slate-600 dark:text-slate-300">{name}</div>
                    <div className={`${refused ? 'whitespace-normal break-words' : 'truncate'} text-[11px] leading-tight tabular-nums ${captionCls}`} aria-live="polite">{uploadCaption(upload)}</div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                    {failed && !refused && onRetryUpload && (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onRetryUpload(); }}
                            className="inline-flex items-center gap-0.5 rounded px-1 text-[11px] font-semibold text-primary hover:bg-primary/10 transition-colors"
                        >
                            <Icon name="refresh" className="!text-[14px]" />
                            {labels.uploadRetry}
                        </button>
                    )}
                    <UploadIndicator progress={upload} />
                    {onCancelUpload && (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onCancelUpload(); }}
                            className="text-slate-400 hover:text-red-500 transition-colors"
                            title={labels.uploadCancel}
                            aria-label={labels.uploadCancel}
                        >
                            <Icon name="close" className="!text-[16px]" />
                        </button>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div
            className={`flex items-center gap-2 rounded border p-1.5 text-xs ${stateCls} ${className}`}
        >
            <Icon name={icon} className="text-slate-400 shrink-0 !text-[16px]" />
            <span className="flex-1 truncate text-slate-600 dark:text-slate-300">{name}</span>
            <div className="flex items-center gap-1.5 shrink-0">
                {onPreview3D && isPrintable3D && (
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onPreview3D(); }}
                        className="text-slate-400 hover:text-primary transition-colors"
                        title={preview3DAriaLabel}
                        aria-label={preview3DAriaLabel}
                    >
                        <Icon name="view_in_ar" className="!text-[16px]" />
                    </button>
                )}
                {state !== 'deleted' && (url || onDownload) && (
                    url ? (
                        <a
                            href={url}
                            download={name}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-slate-400 hover:text-green-500 transition-colors"
                            title={downloadAriaLabel}
                            aria-label={downloadAriaLabel}
                        >
                            <Icon name="download" className="!text-[16px]" />
                        </a>
                    ) : (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onDownload?.(); }}
                            className="text-slate-400 hover:text-green-500 transition-colors"
                            title={downloadAriaLabel}
                            aria-label={downloadAriaLabel}
                        >
                            <Icon name="download" className="!text-[16px]" />
                        </button>
                    )
                )}
                {state === 'deleted' && onUndoDelete && (
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onUndoDelete(); }}
                        className="text-slate-400 hover:text-emerald-500 transition-colors"
                        title={undoAriaLabel}
                        aria-label={undoAriaLabel}
                    >
                        <Icon name="undo" className="!text-[16px]" />
                    </button>
                )}
                {state !== 'deleted' && onDelete && (
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onDelete(); }}
                        className="text-slate-400 hover:text-red-500 transition-colors"
                        title={deleteAriaLabel}
                        aria-label={deleteAriaLabel}
                    >
                        <Icon name="delete" className="!text-[16px]" />
                    </button>
                )}
                {actions}
            </div>
        </div>
    );
};

export default FileAttachmentRow;
